import { Link } from "react-router-dom";
import { Mail, ShieldCheck } from "lucide-react";

export default function FreeEntry() {
  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-20" data-testid="free-entry-page">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">No purchase necessary</div>
      <h1 className="font-display text-4xl md:text-6xl text-white mt-2">Free postal entry</h1>
      <p className="text-white/70 mt-4 max-w-2xl">
        UFR is a skill-based competition and, in line with UK law, a free postal entry route is available for every raffle. You do not have to pay to enter.
      </p>

      <div className="ufr-card rounded-md p-6 md:p-10 mt-10 space-y-8">
        <Section icon={<Mail size={22} />} title="How to enter for free by post">
          <p>To enter any UFR raffle for free, send a sealed envelope by 1st class post containing the following clearly written information:</p>
          <ul className="list-disc list-inside space-y-1 mt-3 text-white/70">
            <li>Your full name</li>
            <li>Your full postal address</li>
            <li>Your email address (so we can contact you if you win)</li>
            <li>Your phone number</li>
            <li>Your date of birth (must be 18+)</li>
            <li>The exact title of the raffle you wish to enter (e.g. "Erling Haaland Topps Chrome Refractor Auto")</li>
            <li>Your answer to the trivia question listed on that raffle's page (A, B, C or D)</li>
          </ul>
          <p className="mt-4">Send your sealed envelope to:</p>
          <div className="font-display text-white text-lg mt-2 leading-tight" data-testid="free-entry-address">
            UFR Free Entry<br />
            Ultimate Football Raffles<br />
            First Floor, Unit L, Alma Park<br />
            Wibtoft, Leicestershire<br />
            LE17 5BH, United Kingdom
          </div>
        </Section>

        <div className="ufr-divider" />

        <Section icon={<ShieldCheck size={22} />} title="Free entry rules">
          <ul className="list-disc list-inside space-y-2 text-white/70">
            <li>One postal entry per person per raffle. Multiple entries in a single envelope or duplicate entries will be disqualified.</li>
            <li>Entries must be handwritten by the entrant. Photocopies, computer printouts and mechanically produced entries will not be accepted.</li>
            <li>Entries must arrive before the closing date and time shown on the raffle's page. Late entries are not eligible and will not be returned.</li>
            <li>Postal entries that contain an incorrect trivia answer will be disqualified, mirroring the skill-based requirement of paid entries.</li>
            <li>Each valid postal entry is allocated one (1) ticket in the relevant raffle, with the same chance of winning as a single paid ticket.</li>
            <li>UFR is not responsible for postal entries that are lost, damaged, delayed or misdirected in the post.</li>
            <li>Entries are accepted from postal addresses worldwide. International postage costs are the responsibility of the entrant.</li>
            <li>Entrants must be aged 18 or over (or the legal age of majority in their country of residence, whichever is higher). Proof of age may be required prior to prize release.</li>
            <li>By entering, you agree to UFR's full Terms &amp; Conditions and Privacy Policy.</li>
          </ul>
        </Section>

        <div className="ufr-divider" />

        <Section title="How winners are drawn">
          <p>All entries — paid and postal — are pooled together and the winner is drawn at random on or after the closing date shown on the raffle's page. Draws are conducted live on stream where possible, and the winner is notified by phone or email within 24 hours. Postal entrants have an equal chance of winning as paid entrants.</p>
        </Section>

        <Section title="Why we offer free entry">
          <p>UK consumer law requires that a competition with a free entry route alongside a paid entry route is classed as a prize competition (or free draw) rather than a lottery. UFR is a skill-based prize competition: a correct trivia answer is required from every entrant, paid or postal. Offering a genuine, equal-chance free postal entry route is part of how we keep UFR fair, transparent and fully compliant.</p>
        </Section>
      </div>

      <div className="mt-10 text-center text-sm text-white/50">
        Questions? All questions and concerns should be sent to <a href="mailto:support@ultimatefootballraffles.com" className="text-[color:var(--ufr-green)] hover:underline">support@ultimatefootballraffles.com</a>
      </div>

      <div className="mt-10 text-center">
        <Link to="/competitions" className="btn-ufr-primary inline-block px-8 py-4 text-sm">Browse live raffles</Link>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className="text-[color:var(--ufr-green)]">{icon}</span>}
        <h2 className="font-display text-2xl text-white">{title}</h2>
      </div>
      <div className="text-white/70 text-sm md:text-base space-y-2 leading-relaxed">{children}</div>
    </div>
  );
}
